import clsx from 'clsx'
import { useFormikContext } from 'formik'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import { Col, Row } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  FormikDateTimeField,
  FormikMultiSelect,
  FormikSingleSelect,
  FormikTextField,
  FormikToggleCheckbox,
  FormikToggleSwitch,
} from '../../../../../components/FormikFields'
import { ModalPanel, Section } from '../../../../../components/ModalSidebar'
import { DEFAULT_SETTINGS_ACTIONS } from '../../constants'
import { OpenSettingsButton } from '../common/DefaultSettingsModalHelpers'
import ScheduleSection from '../common/Scheduling/ScheduleSection'
import { DELETE_USER_PANEL_FIELDS, FORM_FIELDS, INITIAL_VALUES } from './constants'

const propTypes = {
  /**
   * Employee full name
   */
  employeeFullName: PropTypes.string.isRequired, // eslint-disable-line react/forbid-prop-types
  /**
   * options for the employees emails
   */
  employeeEmailOptions: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  /**
   * information of the user to be removed
   */
  userInfo: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
   * indicates if the user has a mailbox
   */
  isUserMailBoxAvailable: PropTypes.bool, // eslint-disable-line react/forbid-prop-types
  /**
   * Indicates if employee has a task scheduled for this action already
   */
  scheduledTask: PropTypes.object, // eslint-disable-line react/forbid-prop-types
}

function DeleteUserPanel(props) {
  const {
    employeeFullName,
    employeeEmailOptions = [],
    userInfo = {},
    isUserMailBoxAvailable = true,
    scheduledTask = null,
  } = props ?? {}
  const { delegatedPermissions, emailAlias } = userInfo

  const intl = useIntl()

  const { values, setTouched, setFieldValue } = useFormikContext()

  const validatePanelFields = useCallback(async () => {
    const errors = await setTouched(DELETE_USER_PANEL_FIELDS, true)
    return !isEmpty(errors) // indicate if form has errors
  }, [setTouched])

  return (
    <ModalPanel
      leftButton="cancel"
      rightButton="validateThenProceed"
      onValidate={validatePanelFields}
      disableNext={!!scheduledTask}
    >
      <ScheduleSection scheduledTask={scheduledTask} />

      <Section>
        <div className="d-flex justify-content-between mb-4">
          <span className="text-strong">
            <FormattedMessage id="GENERAL.EMAIL" />
          </span>

          <OpenSettingsButton
            modalAction={DEFAULT_SETTINGS_ACTIONS.OFFBOARDING}
            headerBackButtonLabel="ENGAGE.OFFBOARD.BACK_TO_OFFBOARD_USER"
          />
        </div>

        <FormikToggleCheckbox
          id={FORM_FIELDS.REMOVE_EMAIL_ALIASES}
          label={<FormattedMessage id="ENGAGE.OFFBOARD.MAKE_ALIAS_AVAILABLE" />}
          disabled={!emailAlias?.length}
          className={clsx('checkbox-left-aligned auto-z-index pt-0', !emailAlias?.length && 'pb-0')}
        />

        {emailAlias?.length ? (
          <ul className="mb-0">
            {emailAlias.map((email) => (
              <li key={email}>{email}</li>
            ))}
          </ul>
        ) : (
          <div className="ms-4 mb-1 text-color-secondary">
            (<FormattedMessage id="ENGAGE.NO_ALIASES" />)
          </div>
        )}

        <FormikToggleCheckbox
          id={FORM_FIELDS.REMOVE_DELEGATE_PERMISSIONS}
          label={<FormattedMessage id="ENGAGE.OFFBOARD.REMOVE_DELEGATE_ACCESS" />}
          disabled={!delegatedPermissions?.length}
          className={clsx('checkbox-left-aligned auto-z-index', !delegatedPermissions?.length && 'pb-0')}
        />

        {delegatedPermissions?.length ? (
          <ul className="mb-0">
            {delegatedPermissions.map((email) => (
              <li key={email}>{email}</li>
            ))}
          </ul>
        ) : (
          <div className="ms-4 mb-1 text-color-secondary">
            (<FormattedMessage id="ENGAGE.NO_MAILBOX_DELEGATE_PERMISSIONS" />)
          </div>
        )}

        <FormikToggleCheckbox
          id={FORM_FIELDS.IS_FOWARDING_MAILBOX}
          label={<FormattedMessage id="ENGAGE.OFFBOARD.FORWARD_EMAILS_TO" />}
          disabled={!isUserMailBoxAvailable}
          className={clsx('checkbox-left-aligned auto-z-index', !isUserMailBoxAvailable && 'pb-0')}
          onChange={(checked) => {
            if (!checked) {
              setFieldValue(FORM_FIELDS.FORWARD_MAILBOX_TO, INITIAL_VALUES[FORM_FIELDS.FORWARD_MAILBOX_TO]) // resets employee selector if checkbox is toggled
            }
          }}
        />

        {!isUserMailBoxAvailable && (
          <div className="ms-4 mb-1 text-color-secondary">
            (<FormattedMessage id="ENGAGE.NO_MAILBOX" />)
          </div>
        )}
        {values[FORM_FIELDS.IS_FOWARDING_MAILBOX] === true && (
          <div className="ms-4">
            <FormikSingleSelect
              id={FORM_FIELDS.FORWARD_MAILBOX_TO}
              defaultLabel={intl.formatMessage({ id: 'GENERAL.SEARCH_FOR_USER' })}
              options={employeeEmailOptions}
            />
          </div>
        )}

        <FormikToggleCheckbox
          id={FORM_FIELDS.HIDDEN_FROM_ADDRESS_LIST_ENABLED}
          label={<FormattedMessage id="ENGAGE.OFFBOARD.HIDDEN_FROM_ADDRESS_LIST_ENABLED" />}
          className={clsx('checkbox-left-aligned auto-z-index', !isUserMailBoxAvailable && 'pb-0')}
          disabled={!isUserMailBoxAvailable}
        />
      </Section>

      <Section>
        <div className="mb-4">
          <div className="d-flex w-100 justify-content-between align-items-start gap-2">
            <span className="text-strong pe-2">
              <FormattedMessage id="ENGAGE.OFFBOARD.CONVERT_TO_SHARED_MAILBOX" />
            </span>
            <FormikToggleSwitch
              id={FORM_FIELDS.CONVERT_TO_SHARED_MAILBOX}
              className="flex-shrink-0"
              disabled={!isUserMailBoxAvailable}
              onChange={(checked) => {
                // reset shared mailbox fields if switch is off
                if (!checked) {
                  setFieldValue(FORM_FIELDS.MAILBOX_USERS, INITIAL_VALUES[FORM_FIELDS.MAILBOX_USERS])
                }
              }}
            />
          </div>
          {!isUserMailBoxAvailable && (
            <div className="text-color-secondary mt-1">
              (<FormattedMessage id="ENGAGE.NO_MAILBOX" />)
            </div>
          )}
        </div>

        {values[FORM_FIELDS.CONVERT_TO_SHARED_MAILBOX] && (
          <>
            {/* mailbox users */}
            <div className="text-bold">
              <FormattedMessage id="ENGAGE.GIVE_ANOTHER_USER_ACCESS_TO_EMAIL" values={{ username: employeeFullName }} />
            </div>
            <div className="text-bold text-color-secondary mb-2">
              <FormattedMessage id="ENGAGE.OFFBOARD.SHARED_MAILBOX_EXPLANATION" />
            </div>
            <div className="ms-4">
              <FormikMultiSelect
                id={FORM_FIELDS.MAILBOX_USERS}
                options={employeeEmailOptions}
                placeholder={intl.formatMessage({ id: 'GENERAL.SEARCH_FOR_USER' })}
              />
            </div>
          </>
        )}
      </Section>

      <Section showSeparatorLine={false}>
        <div className="mb-4">
          <div className="d-flex w-100 justify-content-between align-items-start gap-2">
            <span className="text-strong pe-2">
              <FormattedMessage id="ENGAGE.OUT_OF_OFFICE_SEND_AUTO_REPLIES" />
            </span>

            <FormikToggleSwitch
              id={FORM_FIELDS.SEND_AUTOMATIC_REPLIES}
              className="flex-shrink-0"
              disabled={!isUserMailBoxAvailable}
              onChange={(checked) => {
                // reset auto reply fields if switch is off
                if (!checked) {
                  setFieldValue(
                    FORM_FIELDS.SEND_REPLIES_DURING_PERIOD,
                    INITIAL_VALUES[FORM_FIELDS.SEND_REPLIES_DURING_PERIOD],
                  )
                  setFieldValue(FORM_FIELDS.AUTO_REPLY_START_DATE, INITIAL_VALUES[FORM_FIELDS.AUTO_REPLY_START_DATE])
                  setFieldValue(FORM_FIELDS.AUTO_REPLY_END_DATE, INITIAL_VALUES[FORM_FIELDS.AUTO_REPLY_END_DATE])
                  setFieldValue(FORM_FIELDS.INTERNAL_REPLY_MESSAGE, INITIAL_VALUES[FORM_FIELDS.INTERNAL_REPLY_MESSAGE])
                  setFieldValue(FORM_FIELDS.EXTERNAL_REPLY_MESSAGE, INITIAL_VALUES[FORM_FIELDS.EXTERNAL_REPLY_MESSAGE])
                }
              }}
            />
          </div>
          {!isUserMailBoxAvailable && (
            <div className="text-color-secondary mt-1">
              (<FormattedMessage id="ENGAGE.NO_MAILBOX" />)
            </div>
          )}
        </div>

        {values[FORM_FIELDS.SEND_AUTOMATIC_REPLIES] && (
          <>
            <FormikToggleCheckbox
              id={FORM_FIELDS.SEND_REPLIES_DURING_PERIOD}
              label={<FormattedMessage id="ENGAGE.OUT_OF_OFFICE_SEND_DURING_PERIOD" />}
              className="checkbox-left-aligned auto-z-index pt-0 mb-2"
              onChange={(checked) => {
                // reset date and time fields if checkbox is off
                if (!checked) {
                  setFieldValue(FORM_FIELDS.AUTO_REPLY_START_DATE, INITIAL_VALUES[FORM_FIELDS.AUTO_REPLY_START_DATE])
                  setFieldValue(FORM_FIELDS.AUTO_REPLY_END_DATE, INITIAL_VALUES[FORM_FIELDS.AUTO_REPLY_END_DATE])
                }
              }}
            />

            {values[FORM_FIELDS.SEND_REPLIES_DURING_PERIOD] && (
              <Row>
                <Col md={6}>
                  <FormikDateTimeField
                    id={FORM_FIELDS.AUTO_REPLY_START_DATE}
                    title="ENGAGE.OUT_OF_OFFICE_START_TIME"
                    showTimezone
                  />
                </Col>

                <Col md={6}>
                  <FormikDateTimeField id={FORM_FIELDS.AUTO_REPLY_END_DATE} title="ENGAGE.OUT_OF_OFFICE_END_TIME" />
                </Col>
              </Row>
            )}

            <FormikTextField
              as="textarea"
              rows={3}
              id={FORM_FIELDS.INTERNAL_REPLY_MESSAGE}
              title="ENGAGE.OUT_OF_OFFICE_SEND_MESSAGE_ORGANIZATION"
              placeholder={intl.formatMessage({ id: 'ENGAGE.OUT_OF_OFFICE_SEND_ADD_MESSAGE' })}
            />

            <FormikTextField
              as="textarea"
              rows={3}
              id={FORM_FIELDS.EXTERNAL_REPLY_MESSAGE}
              title="ENGAGE.OUT_OF_OFFICE_SEND_MESSAGE_EXTERNAL"
              placeholder={intl.formatMessage({ id: 'ENGAGE.OUT_OF_OFFICE_SEND_ADD_MESSAGE' })}
            />
          </>
        )}
      </Section>
    </ModalPanel>
  )
}

DeleteUserPanel.propTypes = propTypes

export default DeleteUserPanel
